import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { UserContext } from '../App';
import Logo from './Logo';
import LegalFooter from './LegalFooter';
import ContextSwitcher from './ContextSwitcher';
import NomineeEstateSwitcher from './NomineeEstateSwitcher';
import { Button } from '../ui';

/**
 * NomineeShell
 *
 * Ensures nominee users always see the left-hand navigation pane while in the app.
 * The nominee menu is intentionally smaller than the owner menu:
 * - Dashboard (nominee estate overview)
 * - Account (their own account settings)
 * - Logout (via account menu)
 */
export default function NomineeShell({ children, force = false }) {
  const { currentUser, setCurrentUser } = useContext(UserContext);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 700);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 700);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  if (!currentUser) return null;

  const isNomineeContext = currentUser.type === 'nominee';
  if (!isNomineeContext && !force) {
    // This shell is only for nominee context; owner pages have their own nav.
    return children;
  }

  const sidebarStyle = {
    width: 220,
    background: 'var(--dusk-700)',
    color: '#fff',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    boxShadow: '2px 0 12px rgba(30,40,60,0.04)',
    position: 'fixed',
    left: 0,
    top: 0,
    height: (isMobile ? '100dvh' : '100vh'),
    zIndex: 100,
    padding: 0,
    overflow: 'hidden',
  };
  const mobileSidebarStyle = {
    ...sidebarStyle,
    width: 180,
    minWidth: 0,
    maxWidth: '80vw',
    transition: 'transform 0.3s',
    transform: 'translateX(-100%)',
    zIndex: 2200,
  };
  const mobileSidebarOpenStyle = {
    ...mobileSidebarStyle,
    transform: 'translateX(0)',
  };

  const isOnNomineeDashboard = location.pathname === '/nominee' || location.pathname.startsWith('/nominee/');
  const isOnAccount = location.pathname === '/account' || location.pathname === '/profile';

  // Extract ownerSub from URLs like:
  // /nominee/estate/:ownerSub/financial|legal|other
  const routeOwnerSub = useMemo(() => {
    const m = String(location.pathname || '').match(/^\/nominee\/estate\/([^/]+)\/(financial|legal|other)\b/);
    if (!m) return null;
    try { return decodeURIComponent(m[1]); } catch { return m[1]; }
  }, [location.pathname]);

  // Estate selection lives in the persistent bar above main content (NomineeEstateSwitcher).
  // We still sync the active estate when a user deep-links to a specific estate URL.

  // When user navigates directly to a specific estate URL, best-effort sync the
  // server-side "active estate" so the app stays single-estate-at-a-time.
  useEffect(() => {
    if (!isNomineeContext) return;
    if (!routeOwnerSub) return;
    if (currentUser?.activeOwnerSub === routeOwnerSub) return;
    let cancelled = false;
    (async () => {
      try {
        const resp = await api.post('/auth/switch-role', { active_context: 'nominee', active_owner_sub: routeOwnerSub });
        if (cancelled) return;
        const updated = {
          ...currentUser,
          type: resp?.data?.activeContext || 'nominee',
          activeOwnerSub: resp?.data?.activeOwnerSub ?? routeOwnerSub,
        };
        setCurrentUser(updated);
        try { localStorage.setItem('currentUser', JSON.stringify(updated)); } catch {}
      } catch (_) {
        // Non-fatal; the API call itself is still protected.
      }
    })();
    return () => { cancelled = true; };
  }, [isNomineeContext, routeOwnerSub, currentUser, setCurrentUser]);

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: 'var(--mist-100)' }}>
      {/* Mobile hamburger */}
      {isMobile && !sidebarOpen && (
        <Button
          onClick={() => setSidebarOpen(!sidebarOpen)}
          variant="primary"
          style={{ position: 'fixed', top: 18, left: 18, zIndex: 2100, fontSize: 22, padding: '10px 12px' }}
          aria-label="Open menu"
        >
          ☰
        </Button>
      )}

      {/* Mobile backdrop (closes menu) */}
      {isMobile && sidebarOpen && (
        <div
          onClick={() => setSidebarOpen(false)}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(30,40,60,0.35)',
            zIndex: 2100,
          }}
          aria-hidden="true"
        />
      )}

      {/* Sidebar */}
      <nav
        style={
          isMobile
            ? (sidebarOpen ? mobileSidebarOpenStyle : mobileSidebarStyle)
            : sidebarStyle
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
          {/* Scrollable menu area */}
          <div
            style={{
              flex: 1,
              minHeight: 0,
              overflowY: 'auto',
              overflowX: 'hidden',
              WebkitOverflowScrolling: 'touch',
              padding: '32px 0 0 0',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '0 18px 24px 18px' }}>
              <div style={{ display: 'flex', justifyContent: 'center', flex: 1, minWidth: 0 }}>
                <Logo size={isMobile ? 28 : 36} variant={isMobile ? 'compact' : 'full'} />
              </div>
              {isMobile && (
                <button
                  type="button"
                  onClick={() => setSidebarOpen(false)}
                  aria-label="Close menu"
                  style={{
                    marginLeft: 8,
                    background: 'rgba(255,255,255,0.10)',
                    color: '#fff',
                    border: 'none',
                    borderRadius: 10,
                    padding: '8px 10px',
                    fontWeight: 800,
                    cursor: 'pointer',
                  }}
                >
                  ✕
                </button>
              )}
            </div>

            {/* User indicator */}
            <div style={{ background: 'var(--sage-600)', color: '#fff', borderRadius: 12, padding: '10px 16px', margin: '0 18px 18px 18px', fontSize: 15, fontWeight: 500, display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 6, boxShadow: 'var(--shadow-soft)', overflowWrap: 'anywhere', maxWidth: 'calc(100% - 36px)' }}>
              <div>
                <div>Logged in as:</div>
                <div style={{ fontWeight: 700, lineHeight: 1.2 }}>{currentUser.name}</div>
                <span
                  title={currentUser?.email || ''}
                  style={{
                    fontSize: 13,
                    opacity: 0.85,
                    lineHeight: 1.2,
                    maxWidth: '100%',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    display: 'block',
                  }}
                >
                  {currentUser?.email}
                </span>
                {currentUser.role && <div style={{ fontSize: 13, opacity: 0.75 }}>({currentUser.role})</div>}
              </div>
              {/* Dual users can switch context here */}
              <ContextSwitcher style={{ width: '100%' }} />
            </div>

            {/* Navigation */}
            <div style={{ marginBottom: 24 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--dusk-500)', margin: '0 18px 8px 18px', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                Menu
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Button
                  onClick={() => { navigate('/nominee'); setSidebarOpen(false); }}
                  variant="sidebar"
                  active={isOnNomineeDashboard}
                >
                  Dashboard
                </Button>
                <Button
                  onClick={() => { navigate('/account'); setSidebarOpen(false); }}
                  variant="sidebar"
                  active={isOnAccount}
                >
                  Account
                </Button>
              </div>
            </div>
          </div>

          {/* Pinned bottom actions */}
          <div
            style={{
              flex: '0 0 auto',
              padding: '12px 0 calc(12px + env(safe-area-inset-bottom, 0px)) 0',
              background: 'var(--dusk-700)',
              borderTop: '1px solid rgba(255,255,255,0.10)',
            }}
          >
            <Button
              type="button"
              onClick={() => {
                // Match owner logout UX: force next login prompt if using Hosted UI SSO.
                try { window.sessionStorage.setItem('force_reauth', '1'); } catch {}
                setCurrentUser(null);
                navigate('/logout');
                setSidebarOpen(false);
              }}
              variant="sidebar"
            >
              Log out
            </Button>
          </div>
        </div>
      </nav>

      {/* Main content */}
      <div style={{ flex: 1, marginLeft: isMobile ? 0 : 220, padding: isMobile ? '76px 0 0 0' : '0 0 0 0', minHeight: '100vh', overflow: 'auto', display: 'flex', flexDirection: 'column' }}>
        {isNomineeContext && (
          <div
            style={{
              flex: '0 0 auto',
              padding: isMobile ? '12px 16px' : '16px 24px',
              background: 'var(--mist-50)',
              borderBottom: '1px solid var(--mist-200)',
            }}
          >
            <NomineeEstateSwitcher />
          </div>
        )}
        <div style={{ flex: 1 }}>{children}</div>
        {/* Persistent transparency footer (UK GDPR Art. 13). */}
        <LegalFooter />
      </div>
    </div>
  );
}


